import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BotIcon, 
  WorkflowIcon, 
  DatabaseIcon, 
  WrenchIcon, 
  ShieldCheckIcon, 
  CodeIcon, 
  BrainIcon, 
  CheckCircleIcon,
  SparklesIcon,
  XIcon
} from './Icons';
import { AGENT_FLEET } from '../data/content';

const AgentFleetSection = () => {
  const [selectedAgent, setSelectedAgent] = useState(null);

  const getAgentIcon = (type) => {
    switch (type) {
      case 'PLANNER_AGENT':
        return <WorkflowIcon className="w-6 h-6 text-violet-500" />;
      case 'RAG_AGENT':
        return <DatabaseIcon className="w-6 h-6 text-cyan-500" />;
      case 'TOOL_AGENT':
        return <WrenchIcon className="w-6 h-6 text-emerald-500" />;
      case 'CRITIC_AGENT':
        return <ShieldCheckIcon className="w-6 h-6 text-rose-500" />;
      case 'CODE_AGENT':
        return <CodeIcon className="w-6 h-6 text-amber-500" />;
      case 'MEMORY_AGENT':
        return <BrainIcon className="w-6 h-6 text-blue-500" />;
      default:
        return <BotIcon className="w-6 h-6 text-slate-500" />;
    }
  };

  return (
    <section id="agents" className="py-20 md:py-28 relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-brand-500/10 text-brand-600 dark:text-brand-400 border border-brand-500/20 font-inter">
            <SparklesIcon className="w-3.5 h-3.5" />
            <span>Autonomous Fleet Architecture</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight font-outfit">
            Six Specialized Agents. <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-600 to-accent-500 dark:from-brand-400 dark:to-accent-400">
              One Synchronized Brain.
            </span>
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg font-light leading-relaxed">
            Instead of a monolithic prompt prone to hallucinations, Supreme Adventure decomposes complex workflows across dedicated, purpose-built agent roles.
          </p>
        </div>

        {/* 6 Agents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {AGENT_FLEET.map((agent, index) => (
            <motion.div
              key={agent.type}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
              onClick={() => setSelectedAgent(agent)}
              className="group glass-panel rounded-3xl p-6 sm:p-7 hover:border-brand-500/50 hover:shadow-xl dark:hover:shadow-brand-500/10 transition-all duration-300 flex flex-col justify-between cursor-pointer active:scale-[0.99] specular-border relative overflow-hidden"
            >
              {/* Subtle Ambient Hover Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-brand-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

              <div>
                {/* Card Top: Icon & Badge */}
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-slate-100 dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/80 shadow-sm group-hover:scale-105 transition-transform duration-200">
                    {getAgentIcon(agent.type)}
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                    {agent.badge}
                  </span>
                </div>

                {/* Name & Role */}
                <h3 className="text-xl font-bold text-slate-900 dark:text-white font-outfit mb-1 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                  {agent.name}
                </h3>
                <p className="text-xs font-semibold text-brand-600 dark:text-brand-400 mb-3 font-mono">
                  {agent.type}
                </p>

                {/* Description */}
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-light mb-5">
                  {agent.description}
                </p>

                {/* Capability Chips */}
                <div className="space-y-1.5 mb-6">
                  {agent.capabilities.map((cap, i) => (
                    <div key={i} className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0" />
                      <span>{cap}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sample Task Box */}
              <div className="pt-4 border-t border-slate-150 dark:border-slate-800/80 mt-auto">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                  Sample Execution Task:
                </span>
                <p className="text-[11px] text-slate-700 dark:text-slate-300 font-mono italic bg-slate-100/60 dark:bg-slate-900/60 p-2 rounded-xl border border-slate-200/60 dark:border-slate-800">
                  "{agent.sampleTask}"
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Interactive Modal for Deep Agent Inspection */}
      <AnimatePresence>
        {selectedAgent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedAgent(null)}
              className="absolute inset-0 bg-slate-950/70 backdrop-blur-md"
            />
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative max-w-xl w-full glass-panel rounded-3xl p-6 sm:p-8 z-10 shadow-2xl border border-slate-200 dark:border-slate-800 text-left font-inter"
            >
              <button 
                onClick={() => setSelectedAgent(null)}
                className="absolute top-5 right-5 p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <XIcon className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                  {getAgentIcon(selectedAgent.type)}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white font-outfit">
                    {selectedAgent.name}
                  </h3>
                  <span className="text-xs font-mono text-brand-600 dark:text-brand-400 font-semibold">
                    ROLE: {selectedAgent.type}
                  </span>
                </div>
              </div>

              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6 font-light">
                {selectedAgent.description}
              </p>

              <div className="space-y-4 mb-6">
                <div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                    Key Execution Capabilities:
                  </h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-700 dark:text-slate-300">
                    {selectedAgent.capabilities.map((c, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <CheckCircleIcon className="w-4 h-4 text-emerald-500 shrink-0" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-4 rounded-2xl bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                    Orchestrator Contract:
                  </span>
                  <div className="text-xs font-mono text-slate-800 dark:text-slate-200">
                    Required Gate: <span className="text-brand-500 font-semibold">Strict Dependency & Critic Verified</span>
                  </div>
                  <div className="text-xs font-mono text-slate-500 dark:text-slate-400 mt-1">
                    Multi-Tenant Isolation: <span className="text-emerald-500">ThreadLocal & X-Tenant-ID Enforced</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setSelectedAgent(null)}
                className="w-full py-3 rounded-xl font-semibold text-sm text-white bg-brand-600 hover:bg-brand-500 active:scale-[0.98] transition-all shadow-md"
              >
                Close Inspector
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default AgentFleetSection;
